import Axios from 'axios';
import classNames from 'classnames';
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '../../assets/images/close-image.svg';
import HidePassword from '../../assets/images/hide-password.svg';

import ShowPassword from '../../assets/images/show-password.svg';
import AuthContainer from '../../components/common/auth-container';
import Button from '../../components/Elements/button/button';
import { emailRegEx } from '../../helpers/utils';
import { setUser } from '../../store/features/userSlice';
import api from '../../store/services/index';
import { addToast } from '../../store/features/toastSlice';
import uuid from 'react-uuid';
import Handlebars from 'handlebars';

const Template = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sender, setSender] = useState('info@zervio.com');
  const [subject, setSubject] = useState('Template test');
  const [html, setHtml] = useState('');
  const [event, setEvent] = useState('');
  const [active, setActive] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [eventCursor, setEventCursor] = useState(null);
  const [show, setShow] = useState('EVENT');
  const [data, setData] = useState();
  const [error, setError] = useState();

  const htmlRef = useRef(null);
  const eventRef = useRef(null);

  Handlebars.registerHelper('size', function (collection) {
    if (!collection) {
      return 0;
    }

    // If it's an object, return number of keys
    if (typeof collection === 'object' && !Array.isArray(collection)) {
      return Object.keys(collection).length;
    }

    // If it's an array
    return collection.length || 0;
  });

  Handlebars.registerHelper('formatDate', function (timestamp, pattern = 'yyyy-MM-dd HH:mm:ss', options) {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000); // Unix timestamp in seconds

    // Extract hash parameters
    const locale = options?.hash?.locale || 'default';
    const timeZone = options?.hash?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(date);
    } catch (e) {
      return '';
    }
  });

  const isEmailValid = email => {
    return emailRegEx.test(email);
  };

  const onSend = async e => {
    if (active) {
      try {
        const { data } = await api.post(`/integrations/a78b7f40-3b6c-44a9-a789-bf8fb6378819/action/EMAIL/execute`, {
          action_parameters: {
            EMAIL_TO: email,
            SUBJECT: subject,
            SENDER: sender,
            HTML_BODY: html,
          },
          action_parameters_list: [],
          data: JSON.parse(event),
        });
        dispatch(addToast({ error: false, text: 'Email sent', id: uuid() }));
      } catch (e) {
        console.log('error');
        console.log(e);
      }
    }
  };

  useEffect(() => {
    setActive(
      email &&
        email.length > 2 &&
        isEmailValid(email) &&
        sender &&
        sender.length > 2 &&
        isEmailValid(sender) &&
        subject &&
        subject.length > 2 &&
        html &&
        html.length > 2,
    );
  }, [email, sender, subject, html, event]);

  const updateInputHeight = () => {
    if (htmlRef.current) {
      htmlRef.current.style.height = '0px';
      const scrollHeight = htmlRef.current.scrollHeight;
      htmlRef.current.style.height = scrollHeight + 'px';
      return scrollHeight + 'px';
    }
  };

  useLayoutEffect(() => {
    const input = htmlRef.current;
    if (input) {
      input.setSelectionRange(cursor, cursor);
      updateInputHeight();
    }
  }, [htmlRef, cursor]);

  const updateEventInputHeight = () => {
    if (eventRef.current) {
      eventRef.current.style.height = '0px';
      const scrollHeight = eventRef.current.scrollHeight;
      eventRef.current.style.height = scrollHeight + 'px';
      return scrollHeight + 'px';
    }
  };

  useLayoutEffect(() => {
    const input = eventRef.current;
    if (input) {
      input.setSelectionRange(eventCursor, eventCursor);
      updateEventInputHeight();
    }
  }, [eventRef, eventCursor]);

  return (
    <TemplateWrapper>
      <div className="flex-column flex-1">
        <div className="flex items-center login-form w-full gap-5">
          <input
            placeholder="Email to"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={classNames('input radius-2')}
          />
          <input
            placeholder="Sender"
            value={sender}
            onChange={e => setSender(e.target.value)}
            className={classNames('input radius-2')}
          />
          <input
            placeholder="Subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className={classNames('input radius-2')}
          />
          <div>
            <Button
              className={classNames('primary', !active && 'disabled')}
              size="large"
              label="Send"
              onClick={onSend}
            />
          </div>
        </div>
        <div className="flex mt-10 flex-1">
          <div className="flex-column flex-1">
            <label>Template</label>
            <div className="flex-column flex-1 mt-2">
              <textarea
                ref={htmlRef}
                autoComplete="turnoff"
                className={`textarea w-full description-input scroll-text`}
                onChange={e => {
                  setCursor(e.target.selectionStart);
                  setHtml(e.target.value);
                }}
                placeholder="Enter html"
                value={html}
              />
            </div>
          </div>
          <div className="flex-column flex-1 ml-8">
            <div className="flex-column">
              <div className="flex">
                <label
                  className="cursor"
                  style={{ fontWeight: show === 'EVENT' ? 700 : 400 }}
                  onClick={() => setShow('EVENT')}>
                  Event
                </label>
                <label
                  className="ml-4 cursor"
                  style={{ fontWeight: show === 'PREVIEW' ? 700 : 400 }}
                  onClick={() => setShow('PREVIEW')}>
                  Preview
                </label>
              </div>
              <div className="flex-column mt-2">
                {show === 'EVENT' ? (
                  <textarea
                    ref={eventRef}
                    autoComplete="turnoff"
                    className={`textarea w-full description-input scroll-text`}
                    onChange={e => {
                      setEventCursor(e.target.selectionStart);
                      setEvent(e.target.value);
                    }}
                    placeholder="Enter event"
                    value={event}
                  />
                ) : (
                  <div className="flex-column">
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                )}
              </div>
            </div>
            {error && <label className="mt-10 red-text">{error?.message}</label>}
          </div>
        </div>
      </div>
    </TemplateWrapper>
  );
};

const TemplateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin: 48px;

  .scroll-text {
    overflow-y: scroll;
    max-height: 600px;
  }
`;

export default Template;
